import { useGame, ITEM_NAMES } from '../state/useGame';
import { EQUIP_SLOT_IDS } from '../data';
import type { EquipSlotId } from '../domain/state';
import {
  equipSlotFor,
  itemBonuses,
  totalBonuses,
  type EquipBonuses,
} from '../domain/economy/equipment';

const SLOT_LABELS: Record<EquipSlotId, string> = {
  head: 'Head',
  chest: 'Chest',
  legs: 'Legs',
  feet: 'Feet',
  hands: 'Hands',
  cape: 'Cape',
  neck: 'Neck',
  ring: 'Ring',
  gem: 'Gem',
  weapon: 'Weapon',
  shield: 'Shield',
  ammo: 'Ammo',
  consumable: 'Consumable',
};

function bonusText(b: EquipBonuses): string {
  const parts: string[] = [];
  if (b.attack) parts.push(`Attack +${b.attack}`);
  if (b.defence) parts.push(`Defence +${b.defence}`);
  if (b.hp) parts.push(`HP +${b.hp}`);
  return parts.length > 0 ? parts.join(' · ') : 'no bonuses';
}

/** Equipment: the 13 locked slots, current bonus totals, and safe equip/unequip. */
export function EquipmentPage() {
  const equipment = useGame((s) => s.state.equipment);
  const inventory = useGame((s) => s.state.inventory);
  const unequipSlot = useGame((s) => s.unequipSlot);
  const equipFromSlot = useGame((s) => s.equipFromSlot);

  const totals = totalBonuses(equipment);
  const equippables = inventory
    .map((stack, index) => ({ stack, index }))
    .filter((e): e is { stack: NonNullable<typeof e.stack>; index: number } =>
      e.stack !== null && equipSlotFor(e.stack.itemId) !== null,
    );

  return (
    <>
      <h1 className="page-title">Equipment</h1>
      <p className="page-subtitle">
        13 slots. Equipping moves one item from your inventory or bank; anything displaced
        returns to the inventory or the swap is refused.
      </p>

      <section className="card" aria-label="Equipment bonuses">
        <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Current totals</h2>
        <p style={{ margin: 0 }}>
          Attack +{totals.attack} · Defence +{totals.defence} · HP +{totals.hp}
        </p>
      </section>

      <div className="equip-grid">
        {EQUIP_SLOT_IDS.map((slot) => {
          const itemId = equipment[slot];
          return (
            <div
              key={slot}
              role="group"
              className="card equip-slot"
              aria-label={`Equipment slot ${SLOT_LABELS[slot]}: ${itemId ? ITEM_NAMES[itemId] : 'empty'}`}
            >
              <div style={{ fontSize: 12, color: 'var(--ui-text-dim)' }}>{SLOT_LABELS[slot]}</div>
              <div
                style={{
                  marginTop: 6,
                  fontWeight: 600,
                  color: itemId ? 'var(--accent-nous)' : 'var(--ui-text-dim)',
                }}
              >
                {itemId ? ITEM_NAMES[itemId] : '— empty —'}
              </div>
              {itemId && (
                <div style={{ fontSize: 11, color: 'var(--ui-text-dim)', marginTop: 4 }}>
                  {bonusText(itemBonuses(itemId))}
                </div>
              )}
              {itemId && (
                <button
                  className="danger"
                  style={{ marginTop: 8 }}
                  aria-label={`Unequip ${ITEM_NAMES[itemId]} from ${SLOT_LABELS[slot]}`}
                  onClick={() => unequipSlot(slot)}
                >
                  Unequip
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Equip from inventory</h2>
        {equippables.length === 0 ? (
          <p style={{ color: 'var(--ui-text-dim)', margin: 0 }}>
            No equippable gear in the inventory. Gear drops from training — check the{' '}
            <strong>Bank</strong>.
          </p>
        ) : (
          <ul className="equip-list">
            {equippables.map(({ stack, index }) => {
              const slot = equipSlotFor(stack.itemId)!;
              const candidate = itemBonuses(stack.itemId);
              const current = equipment[slot] ? itemBonuses(equipment[slot]) : null;
              return (
                <li key={index}>
                  <span>
                    <strong>{ITEM_NAMES[stack.itemId]}</strong> ×{stack.qty} → {SLOT_LABELS[slot]}
                    <span style={{ color: 'var(--ui-text-dim)', marginLeft: 8, fontSize: 12 }}>
                      {bonusText(candidate)}
                      {current
                        ? ` (equipped: ${bonusText(current)})`
                        : ' (slot empty)'}
                    </span>
                  </span>
                  <button onClick={() => equipFromSlot({ container: 'inventory', index })}>
                    Equip {ITEM_NAMES[stack.itemId]}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
